import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { ChevronLeft, Clock, MapPin, MessageCircle, Sparkles, ArrowRight, Copy } from "lucide-react";
import { resolveBusinessConfig } from "@/config/businessTypes";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1773904215697-e6c21fc27ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYmVhdXR5JTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODUwOTE2OTR8MA&ixlib=rb-4.1.0&q=85";

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4].map((n) => (
        <div
          key={n}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            n === step ? "w-8 bg-gold" : n < step ? "w-3 bg-cyan" : "w-3 bg-neutral-700"
          }`}
        />
      ))}
    </div>
  );
}

export default function PublicBooking() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [client, setClient] = useState({ name: "", phone: "" });
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.get(`/public/business/${slug}`)
      .then(({ data }) => setBiz(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Negocio no encontrado"));
  }, [slug]);

  useEffect(() => {
    if (!selectedService || !date) return;
    setLoadingSlots(true);
    setSelectedTime(null);
    const d = format(date, "yyyy-MM-dd");
    api.get(`/public/business/${slug}/slots`, { params: { date_str: d, service_id: selectedService.id } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedService, date, slug]);

  const deposit = useMemo(() => {
    if (!biz || !selectedService) return 0;
    return Math.round(selectedService.price * (biz.deposit_percent / 100) * 100) / 100;
  }, [biz, selectedService]);

  const disabledDay = (d) => {
    if (!biz) return true;
    const day = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][d.getDay()];
    const s = biz.schedule?.[day];
    if (!s || s.closed) return true;
    const today = new Date(); today.setHours(0,0,0,0);
    return d < today;
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const payload = {
        client_name: client.name.trim(),
        client_phone: client.phone.trim(),
        date: format(date, "yyyy-MM-dd"),
        time: selectedTime,
        service_id: selectedService.id,
      };
      const { data } = await api.post(`/public/business/${slug}/appointments`, payload);
      const wa = (biz.whatsapp || "").replace(/\D/g, "");
      const msg = encodeURIComponent(
        `Hola ${biz.name}! Soy ${client.name}. Reservé ${selectedService.name} el ${payload.date} a las ${payload.time}. Anticipo de $${data.deposit}. Envío comprobante ahora. ✨`
      );
      const waUrl = wa ? `https://wa.me/${wa}?text=${msg}` : null;
      navigate(`/agenda/${slug}/exito`, { state: { apt: data, biz, waUrl } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setConfirming(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-void grain flex items-center justify-center p-8">
        <div className="text-center">
          <p className="overline mb-3">Ups</p>
          <h1 className="font-display text-3xl">{error}</h1>
        </div>
      </div>
    );
  }

  if (!biz) {
    return <div className="min-h-screen bg-void flex items-center justify-center text-gold font-display text-2xl animate-pulse">Cargando…</div>;
  }

  const cfg = resolveBusinessConfig(biz);
  const term = cfg.terminology;

  if (!biz.active) {
    return (
      <div className="min-h-screen bg-void grain flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <p className="overline text-neonpink mb-3">No disponible</p>
          <h1 className="font-display text-3xl">Este negocio está temporalmente inactivo.</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void text-neutral-100 grain flex flex-col">
      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${HERO_FALLBACK})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 to-void" />
        <div className="relative max-w-3xl mx-auto p-6 pt-10 pb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-surface gold-border flex items-center justify-center overflow-hidden">
              {biz.logo_base64 ? (
                <img src={biz.logo_base64} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <Sparkles className="w-6 h-6 text-gold" strokeWidth={1.5} />
              )}
            </div>
            <div className="min-w-0">
              <p className="overline mb-1">{cfg.copy.publicHeader}</p>
              <h1 className="font-display text-3xl md:text-4xl leading-tight truncate" data-testid="public-business-name">{biz.name}</h1>
              {biz.address && (
                <div className="text-neutral-400 text-xs mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> <span className="truncate">{biz.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="text-cyan text-sm flex items-center gap-1" data-testid="back-step-btn">
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
          ) : <div />}
          <StepDots step={step} />
        </div>

        {step === 1 && (
          <section className="animate-fade-up">
            <p className="overline mb-2">Paso 1</p>
            <h2 className="font-display text-3xl mb-6">Elige un {term.service}</h2>
            <div className="space-y-3">
              {(biz.services || []).length === 0 ? (
                <div className="text-neutral-500">Este negocio aún no tiene {term.servicePlural} configurados.</div>
              ) : biz.services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSelectedService(s); setStep(2); }}
                  data-testid={`service-${s.id}`}
                  className="w-full text-left bg-surface gold-border rounded-2xl p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-transform duration-200"
                >
                  <div className="flex-1">
                    <div className="font-display text-xl">{s.name}</div>
                    <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {s.duration} min
                    </div>
                  </div>
                  <div className="text-gold font-display text-2xl">${s.price}</div>
                  <ArrowRight className="w-5 h-5 text-cyan" />
                </button>
              ))}
            </div>
          </section>
        )}

        {step === 2 && selectedService && (
          <section className="animate-fade-up">
            <p className="overline mb-2">Paso 2</p>
            <h2 className="font-display text-3xl mb-6">Elige fecha y hora</h2>
            <div className="bg-surface gold-border rounded-2xl p-4 mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 capitalize">{term.service}</div>
                <div className="font-display text-xl">{selectedService.name}</div>
              </div>
              <div className="text-gold font-display text-2xl">${selectedService.price}</div>
            </div>

            <div className="bg-surface gold-border rounded-2xl p-4 mb-4 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={disabledDay}
                locale={es}
                data-testid="booking-calendar"
              />
            </div>

            {date && (
              <div className="animate-fade-up">
                <div className="overline mb-3">Horarios disponibles · {format(date, "EEEE d 'de' MMMM", { locale: es })}</div>
                {loadingSlots ? (
                  <div className="text-neutral-500 text-sm">Cargando horarios…</div>
                ) : slots.length === 0 ? (
                  <div className="text-neutral-500 text-sm bg-surface p-4 rounded-xl gold-border">
                    Sin horarios disponibles ese día. Prueba otra fecha.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {slots.map((t) => (
                      <button
                        key={t}
                        onClick={() => setSelectedTime(t)}
                        data-testid={`slot-${t}`}
                        className={`px-3 py-3 rounded-xl font-mono text-sm transition-colors duration-200 border ${
                          selectedTime === t
                            ? "bg-neonpink text-void border-neonpink"
                            : "bg-surface text-neutral-200 border-gold/15 hover:border-gold/50"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button
              disabled={!selectedTime}
              onClick={() => setStep(3)}
              className="w-full mt-6 bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12"
              data-testid="continue-to-form-btn"
            >
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </section>
        )}

        {step === 3 && (
          <section className="animate-fade-up">
            <p className="overline mb-2">Paso 3</p>
            <h2 className="font-display text-3xl mb-6">Tus datos</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-400">Nombre completo</Label>
                <Input
                  value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })}
                  className="mt-2 bg-surface border-gold/20 h-12" data-testid="client-name-input"
                  placeholder="María López"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-400">WhatsApp</Label>
                <Input
                  value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })}
                  className="mt-2 bg-surface border-gold/20 h-12" data-testid="client-phone-input"
                  placeholder="+52 729 755 3698"
                />
              </div>
            </div>
            <Button
              disabled={!client.name || !client.phone}
              onClick={() => setStep(4)}
              className="w-full mt-6 bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12"
              data-testid="continue-to-summary-btn"
            >
              Ver resumen <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </section>
        )}

        {step === 4 && (
          <section className="animate-fade-up">
            <p className="overline mb-2">Paso 4</p>
            <h2 className="font-display text-3xl mb-6">Confirmar {term.appointment}</h2>
            <div className="bg-surface gold-border rounded-2xl p-6 mb-4">
              <p className="text-neutral-300 leading-relaxed" data-testid="booking-summary">
                Para confirmar tu {term.appointment} de <span className="text-gold">{selectedService.name}</span> el{" "}
                <span className="text-cyan">{format(date, "EEEE d 'de' MMMM", { locale: es })}</span> a las{" "}
                <span className="text-cyan">{selectedTime}</span>, paga anticipo de{" "}
                <span className="font-display text-2xl text-gold">${deposit}</span>.
              </p>
            </div>
            <div className="bg-surface gold-border rounded-2xl p-6 mb-4 space-y-3" data-testid="payment-info-block">
              <div className="overline">Datos para transferencia bancaria</div>
              {(() => {
                const pi = biz.payment_info || {};
                const fields = [
                  { k: "bank", l: "Banco", mono: false },
                  { k: "account_holder", l: "Titular", mono: false },
                  { k: "clabe", l: "CLABE", mono: true, copy: true },
                  { k: "account_number", l: "Número de cuenta", mono: true, copy: true },
                  { k: "card_number", l: "Número de tarjeta", mono: true, copy: true },
                  { k: "reference", l: "Referencia", mono: false },
                ];
                const visible = fields.filter((f) => (pi[f.k] || "").toString().trim() !== "");
                if (visible.length === 0 && !pi.additional_instructions && !pi.mp_link) {
                  return (
                    <div className="text-neutral-500 text-sm">
                      El negocio aún no configuró sus datos de pago. Contáctalo por WhatsApp.
                    </div>
                  );
                }
                return (
                  <>
                    {visible.map((f) => (
                      <div key={f.k} className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs uppercase tracking-widest text-neutral-500">{f.l}</div>
                          <div className={`${f.mono ? "font-mono" : ""} text-sm break-all`}>{pi[f.k]}</div>
                        </div>
                        {f.copy && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-cyan hover:text-gold shrink-0"
                            onClick={() => { navigator.clipboard.writeText(pi[f.k]); toast.success(`${f.l} copiada`); }}
                            data-testid={`copy-${f.k}-btn`}
                          >
                            <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                          </Button>
                        )}
                      </div>
                    ))}
                    {pi.additional_instructions && (
                      <div>
                        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Instrucciones</div>
                        <div className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">
                          {pi.additional_instructions}
                        </div>
                      </div>
                    )}
                    {pi.mp_link && (
                      <a href={pi.mp_link} target="_blank" rel="noreferrer" data-testid="mp-pay-link" className="block pt-1">
                        <Button variant="outline" className="w-full border-cyan/40 text-cyan hover:bg-cyan/10 rounded-full">
                          Pagar con Mercado Pago
                        </Button>
                      </a>
                    )}
                  </>
                );
              })()}
            </div>

            <Button
              onClick={confirm} disabled={confirming}
              className="w-full bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-14 text-base"
              data-testid="confirm-appointment-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> {confirming ? "Guardando…" : `Confirmar ${term.appointment} por WhatsApp`}
            </Button>
            <p className="text-[11px] uppercase tracking-widest text-neutral-500 text-center mt-3">
              Serás redirigido a WhatsApp para enviar tu comprobante
            </p>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
